import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { parseFeatureId } from "../helpers/feature-id"
import {
  LIST_PAGE_SIZE,
  NEAR_RADIUS_STOPS_MI,
  VALID_LIST_PAGE_SIZES
} from "../redux/global-map.constants"
import {
  selectActiveFilters,
  selectActiveLayers,
  selectActiveTab,
  selectFeature,
  selectListCurrentPage,
  selectListPageSize,
  selectSelectedFeatureId,
  setActiveLayers,
  setActiveTab,
  setFilters,
  setListPage,
  setListPageSize
} from "../redux/global-map.slice"
import type {
  EntityType,
  GlobalMapFilters,
  GlobalMapState
} from "../redux/global-map.types"

const LAYERS_PARAM = "layers"
const FEATURE_PARAM = "feature"
const TAB_PARAM = "tab"
const PAGE_PARAM = "page"
const PAGE_SIZE_PARAM = "pageSize"
const VALID_LAYERS: EntityType[] = ["project", "lead"]
// `"map"` (the default) is deliberately never read or written here — only
// `"list"`/`"analytics"` round-trip through the URL, the same "default
// elides itself" convention every other param in this hook already
// follows.
const VALID_URL_TABS: GlobalMapState["activeTab"][] = ["list", "analytics"]

const FILTER_PARAMS = {
  salespersonIds: "salesperson",
  projectManagerIds: "project_manager",
  teamMemberIds: "team_member",
  subcontractorIds: "subcontractor",
  projectStatusIds: "project_status",
  leadStatusIds: "lead_status",
  projectTypeIds: "type",
  region: "region",
  activePeriodStart: "active_period_start",
  activePeriodEnd: "active_period_end",
  nearZip: "near_zip",
  nearRadiusMi: "near_radius_mi",
  hasUpcomingAppointment: "has_upcoming_appointment",
  includeHistory: "include_history"
} as const satisfies Record<keyof GlobalMapFilters, string>

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

// Anything that isn't one of the two known layer values is dropped rather
// than rejecting the whole param — `?layers=lead,bogus` still resolves to
// `["lead"]` instead of falling all the way back to default. Duplicates
// collapse too (`?layers=lead,lead` → `["lead"]`) since this feeds straight
// into `setActiveLayers`, and a repeated entry is otherwise indistinguishable
// from a real second layer once it's in the array. Returns null (never
// throws, whatever the input) when nothing usable survives — no param, an
// empty value, or every part unrecognized — so the caller can fall back to
// whatever's already in the store (a persisted value, or DEFAULT_LAYERS via
// the slice's own initial state) instead of a bare `?layers=` param silently
// forcing the map to zero layers on load.
function parseLayersParam(raw: string | null): EntityType[] | null {
  if (!raw) return null
  const parts = raw
    .split(",")
    .filter((part): part is EntityType =>
      VALID_LAYERS.includes(part as EntityType)
    )
  return parts.length > 0 ? Array.from(new Set(parts)) : null
}

// Same tolerant, per-token philosophy as `parseLayersParam`: a malformed
// token is dropped rather than rejecting the whole param, and an empty
// result (no param, an empty value, or every token malformed) returns null
// so the caller leaves the store's existing value alone instead of
// dispatching a zero-length array over a possibly-already-set one.
function parseNumberListParam(raw: string | null): number[] | null {
  if (!raw) return null
  const parts = raw
    .split(",")
    .filter((part) => /^\d+$/.test(part))
    .map(Number)
  return parts.length > 0 ? parts : null
}

// Same tolerant, per-token philosophy as `parseLayersParam`/`parseNumberListParam`:
// each token is trimmed (a stray space after a comma, e.g. `?type=kitchen,
// bath`, must not survive into a stored value) and duplicates collapse via
// `Set`, matching `parseLayersParam`'s existing precedent exactly.
function parseStringListParam(raw: string | null): string[] | null {
  if (!raw) return null
  const parts = raw
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
  return parts.length > 0 ? Array.from(new Set(parts)) : null
}

// Trims before checking for content, so a whitespace-only value (e.g. a
// literal space from `?region=%20`) is treated the same as an empty one —
// null, never a "valid" value that goes on to get stored, written back to
// the URL and sent to the backend as a real filter.
function parseStringParam(raw: string | null): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

// Rejects anything that isn't the `YYYY-MM-DD` shape, and also anything that
// is that shape but not a real calendar date (e.g. `2026-02-30`) — `Date`
// silently rolls an invalid day/month forward rather than rejecting it, so
// the round-trip check below is what actually catches that case.
function parseIsoDateParam(raw: string | null): string | null {
  if (!raw || !ISO_DATE_RE.test(raw)) return null
  const [year, month, day] = raw.split("-").map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  const roundTrips =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  return roundTrips ? raw : null
}

// Only `"list"`/`"analytics"` are ever valid URL values — see
// `VALID_URL_TABS`'s own comment for why `"map"` (the default) is excluded.
// Anything else (missing param, `"map"`, or garbage) returns null so the
// caller leaves the store's existing `activeTab` alone, same
// null-means-leave-it contract every other parser here follows.
function parseTabParam(raw: string | null): GlobalMapState["activeTab"] | null {
  if (!raw) return null
  return VALID_URL_TABS.includes(raw as GlobalMapState["activeTab"])
    ? (raw as GlobalMapState["activeTab"])
    : null
}

// Task 9 — a page must be a positive integer; anything else (missing, zero,
// negative, non-numeric) returns null so the caller leaves the store's
// existing `currentPage` alone, same null-means-leave-it contract every
// parser here follows.
function parsePageParam(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const page = Number(raw)
  return page >= 1 ? page : null
}

// Task 9 — only 5/10/20/50 are valid page sizes (`VALID_LIST_PAGE_SIZES`'s
// own comment) — the backend 400s on anything else.
function parsePageSizeParam(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const pageSize = Number(raw)
  return (VALID_LIST_PAGE_SIZES as readonly number[]).includes(pageSize)
    ? pageSize
    : null
}

// "Look Around a Zip Code" — a plain 5-digit zip; anything else (missing,
// malformed) returns null, same null-means-leave-it contract every parser
// here follows. No further validation (whether it's a REAL zip) happens
// client-side — that's the backend's job, at Apply time, not the URL reader's.
function parseNearZipParam(raw: string | null): string | null {
  if (!raw || !/^\d{5}$/.test(raw)) return null
  return raw
}

// Only `NEAR_RADIUS_STOPS_MI`'s six fixed values are valid — the backend
// 400s on anything else, same closed-set contract `parsePageSizeParam`
// already follows for page sizes.
function parseNearRadiusParam(raw: string | null): number | null {
  if (!raw) return null
  const radius = Number(raw)
  return (NEAR_RADIUS_STOPS_MI as readonly number[]).includes(radius)
    ? radius
    : null
}

// Never throws, whatever `raw` is — `parseFeatureId` throws on an
// unrecognized prefix, so that's caught here and folded into the same
// "return null, caller leaves the store's existing selection alone" contract
// every other parser in this file follows.
function parseFeatureParam(
  raw: string | null
): { id: string; entityType: EntityType } | null {
  if (!raw) return null
  try {
    const { entityType, id } = parseFeatureId(raw)
    if (!id) return null
    return { id: raw, entityType }
  } catch {
    return null
  }
}

// Builds only the keys that parsed successfully — an omitted key means
// "leave whatever's already in the store," which is what lets a garbage
// `?status=` coexist with a valid `?region=` in the same URL without either
// one clobbering the other's outcome.
function parseFiltersParams(
  searchParams: URLSearchParams
): Partial<GlobalMapFilters> {
  const filters: Partial<GlobalMapFilters> = {}

  const salespersonIds = parseStringListParam(
    searchParams.get(FILTER_PARAMS.salespersonIds)
  )
  if (salespersonIds) filters.salespersonIds = salespersonIds

  const projectManagerIds = parseStringListParam(
    searchParams.get(FILTER_PARAMS.projectManagerIds)
  )
  if (projectManagerIds) filters.projectManagerIds = projectManagerIds

  const teamMemberIds = parseStringListParam(
    searchParams.get(FILTER_PARAMS.teamMemberIds)
  )
  if (teamMemberIds) filters.teamMemberIds = teamMemberIds

  const subcontractorIds = parseStringListParam(
    searchParams.get(FILTER_PARAMS.subcontractorIds)
  )
  if (subcontractorIds) filters.subcontractorIds = subcontractorIds

  const projectStatusIds = parseNumberListParam(
    searchParams.get(FILTER_PARAMS.projectStatusIds)
  )
  if (projectStatusIds) filters.projectStatusIds = projectStatusIds

  const leadStatusIds = parseNumberListParam(
    searchParams.get(FILTER_PARAMS.leadStatusIds)
  )
  if (leadStatusIds) filters.leadStatusIds = leadStatusIds

  const projectTypeIds = parseStringListParam(
    searchParams.get(FILTER_PARAMS.projectTypeIds)
  )
  if (projectTypeIds) filters.projectTypeIds = projectTypeIds

  const region = parseStringListParam(searchParams.get(FILTER_PARAMS.region))
  if (region) filters.region = region

  const activePeriodStart = parseIsoDateParam(
    searchParams.get(FILTER_PARAMS.activePeriodStart)
  )
  if (activePeriodStart) filters.activePeriodStart = activePeriodStart

  const activePeriodEnd = parseIsoDateParam(
    searchParams.get(FILTER_PARAMS.activePeriodEnd)
  )
  if (activePeriodEnd) filters.activePeriodEnd = activePeriodEnd

  // All-or-nothing, same convention as the bbox fields — a URL carrying only
  // one half of the pair (a hand-edited link, a stale bookmark) must never
  // reach the store as a half-set filter the backend would 400 on.
  const nearZip = parseNearZipParam(searchParams.get(FILTER_PARAMS.nearZip))
  const nearRadiusMi = parseNearRadiusParam(
    searchParams.get(FILTER_PARAMS.nearRadiusMi)
  )
  if (nearZip && nearRadiusMi) {
    filters.nearZip = nearZip
    filters.nearRadiusMi = nearRadiusMi
  }

  if (searchParams.get(FILTER_PARAMS.hasUpcomingAppointment) === "true") {
    filters.hasUpcomingAppointment = true
  }

  if (searchParams.get(FILTER_PARAMS.includeHistory) === "true") {
    filters.includeHistory = true
  }

  return filters
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | undefined | null
) {
  if (value) params.set(key, value)
  else params.delete(key)
}

function applyLayersParam(params: URLSearchParams, layers: EntityType[]) {
  params.set(LAYERS_PARAM, layers.join(","))
}

function applyFiltersParams(
  params: URLSearchParams,
  filters: GlobalMapFilters
) {
  setOrDelete(
    params,
    FILTER_PARAMS.salespersonIds,
    filters.salespersonIds.length ? filters.salespersonIds.join(",") : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.projectManagerIds,
    filters.projectManagerIds.length
      ? filters.projectManagerIds.join(",")
      : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.teamMemberIds,
    filters.teamMemberIds.length ? filters.teamMemberIds.join(",") : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.subcontractorIds,
    filters.subcontractorIds.length
      ? filters.subcontractorIds.join(",")
      : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.projectStatusIds,
    filters.projectStatusIds.length
      ? filters.projectStatusIds.join(",")
      : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.leadStatusIds,
    filters.leadStatusIds.length ? filters.leadStatusIds.join(",") : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.projectTypeIds,
    filters.projectTypeIds.length ? filters.projectTypeIds.join(",") : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.region,
    filters.region.length ? filters.region.join(",") : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.activePeriodStart,
    filters.activePeriodStart ?? undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.activePeriodEnd,
    filters.activePeriodEnd ?? undefined
  )
  setOrDelete(params, FILTER_PARAMS.nearZip, filters.nearZip ?? undefined)
  setOrDelete(
    params,
    FILTER_PARAMS.nearRadiusMi,
    filters.nearRadiusMi != null ? String(filters.nearRadiusMi) : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.hasUpcomingAppointment,
    filters.hasUpcomingAppointment ? "true" : undefined
  )
  setOrDelete(
    params,
    FILTER_PARAMS.includeHistory,
    filters.includeHistory ? "true" : undefined
  )
}

function applyFeatureParam(
  params: URLSearchParams,
  selectedFeatureId: string | null
) {
  setOrDelete(params, FEATURE_PARAM, selectedFeatureId ?? undefined)
}

// `"map"`, the default, is omitted from the URL entirely rather than written
// as `?tab=map` — the same elide-the-default convention every other
// default-valued param in this hook already follows.
function applyTabParam(
  params: URLSearchParams,
  activeTab: GlobalMapState["activeTab"]
) {
  setOrDelete(
    params,
    TAB_PARAM,
    VALID_URL_TABS.includes(activeTab) ? activeTab : undefined
  )
}

// Task 9 — `1`, the default, is omitted entirely, same elide-the-default
// convention as `applyTabParam` above.
function applyPageParam(params: URLSearchParams, currentPage: number) {
  setOrDelete(
    params,
    PAGE_PARAM,
    currentPage !== 1 ? String(currentPage) : undefined
  )
}

// Task 9 — `LIST_PAGE_SIZE`, the default, is likewise omitted.
function applyPageSizeParam(params: URLSearchParams, pageSize: number) {
  setOrDelete(
    params,
    PAGE_SIZE_PARAM,
    pageSize !== LIST_PAGE_SIZE ? String(pageSize) : undefined
  )
}

// Order-independent equality — both sides are always built from the same
// fixed sequence of `set`/`delete` calls in practice, but comparing by
// entries (rather than `.toString()`) keeps the idempotency check correct
// even if that ever stops being true.
function paramsEqual(a: URLSearchParams, b: URLSearchParams): boolean {
  const entriesA = [...a.entries()].sort(([ka], [kb]) => ka.localeCompare(kb))
  const entriesB = [...b.entries()].sort(([ka], [kb]) => ka.localeCompare(kb))
  if (entriesA.length !== entriesB.length) return false
  return entriesA.every(
    ([k, v], i) => entriesB[i][0] === k && entriesB[i][1] === v
  )
}

// Compares only the keys `expected` actually names — an untouched filter is
// never part of `expected` to begin with (see `parseFiltersParams`), so it
// can never block this from resolving.
function filtersMatch(
  current: GlobalMapFilters,
  expected: Partial<GlobalMapFilters>
): boolean {
  return (Object.keys(expected) as (keyof GlobalMapFilters)[]).every((key) => {
    const currentValue = current[key]
    const expectedValue = expected[key]
    if (Array.isArray(currentValue) && Array.isArray(expectedValue)) {
      return currentValue.join(",") === expectedValue.join(",")
    }
    return currentValue === expectedValue
  })
}

/**
 * Keeps `?layers=`, the filter params (`?region=`, `?status=`, ...),
 * `?feature=` and `?tab=` in sync with the store, one direction at a time —
 * the generalized form of what `useLayerUrlSync` did for `layers` alone.
 * `search` is deliberately never read or written here; it lives in
 * `GlobalMapState` outside `committedQuery.filters` specifically so a
 * customer name typed into search can never land in browser history or a
 * shared link (see `GlobalMapCommittedQuery`'s own comment). `?tab=` follows
 * its own, narrower rule: `"list"`/`"analytics"` round-trip through the
 * URL — `"map"` is the default and elides itself like every other
 * default-valued param here (see `VALID_URL_TABS`).
 *
 * On mount, each param is read exactly once to seed the store — anything
 * missing or invalid is ignored per-field, leaving whatever was already
 * there. From then on, every change to the store's layers/filters/selected
 * feature/active tab writes back to the URL via `replace`, never `push`.
 *
 * Increment 2's final review found and fixed a real double-write-on-mount
 * race in the single-param ancestor of this hook (`ad1662b94`): the
 * read effect's dispatch and the write effect's first pass share the same
 * React commit, so without a guard, the write effect's first pass sees the
 * stale, pre-dispatch store value and briefly clobbers the very override the
 * read effect just applied. Generalizing to layers + filters + feature
 * multiplies the surface for the exact same race, so each field-group gets
 * its own `pendingUrlOverride`-shaped ref below, all checked before the
 * write effect is allowed to call `setSearchParams`.
 *
 * The layers guard specifically converges on EXACT equality with the value
 * it dispatched — that's only safe as long as nothing else ever dispatches a
 * *different* `setActiveLayers` before that value lands. The final
 * whole-branch review (Increment 3) found a real case where that assumption
 * broke: a `?layers=project&feature=l:<uuid>` deep link (a lead, reachable
 * with only the Projects layer active) needs its layers widened to
 * `["project","lead"]` so the deep-linked lead is included in quick-view
 * Previous/Next — but the widening used to happen in a SEPARATE `MapTab`
 * effect, dispatched after this hook's own mount read, so the guard's
 * pending value (`["project"]`) could never match the store's actual value
 * (`["project","lead"]`) and every future URL write silently died for the
 * rest of the session. Folding the widening into this same mount-time read
 * — computed and dispatched together with everything else below, before
 * `pendingLayersOverride` is ever set — closes that gap structurally: the
 * guard's pending value IS the widened value, so there is no later,
 * divergent dispatch left for it to miss.
 */
export interface UseGlobalMapUrlSyncResult {
  isReady: boolean
  // See `pageSizeFromUrl` state's own comment inside the hook.
  pageSizeFromUrl: number | null
}

export function useGlobalMapUrlSync(): UseGlobalMapUrlSyncResult {
  const [searchParams, setSearchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const activeLayers = useAppSelector(selectActiveLayers)
  const filters = useAppSelector(selectActiveFilters)
  const selectedFeatureId = useAppSelector(selectSelectedFeatureId)
  const activeTab = useAppSelector(selectActiveTab)
  const currentPage = useAppSelector(selectListCurrentPage)
  const pageSize = useAppSelector(selectListPageSize)

  const hasReadInitialUrl = useRef(false)
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  const pendingLayersOverride = useRef<EntityType[] | null>(null)
  const pendingFiltersOverride = useRef<Partial<GlobalMapFilters> | null>(null)
  const pendingFeatureOverride = useRef<string | undefined>(undefined)
  const pendingTabOverride = useRef<GlobalMapState["activeTab"] | null>(null)
  const pendingPageOverride = useRef<number | null>(null)
  const pendingPageSizeOverride = useRef<number | null>(null)

  // Signals "the mount-time URL read has run" to callers whose own initial
  // fetch would otherwise fire once against the pre-read (default) store
  // values and again once this hook's dispatches land — see `MapTab.tsx`'s
  // `useMapFeatures`/`useGlobalMapFacets` call sites. A ref alone can't
  // notify anyone (no re-render), and this has to be real state rather than
  // relying on the redux dispatches below to cause a re-render, since a URL
  // with no recognized params at all makes none of those dispatches fire.
  const [isReady, setIsReady] = useState(false)

  // Fix (review round) — a real "was `?pageSize=` present on the URL at
  // mount" signal, frozen once read (never re-derived on a later render,
  // same as every other mount-time value in this hook). `useListFeatures`
  // needs this explicitly: comparing the resolved `pageSize` against
  // `LIST_PAGE_SIZE` can't tell "no URL override" apart from "the URL
  // explicitly set `?pageSize=10`, which happens to equal the app default" —
  // in the second case a differing persisted `ui-preferences` choice must
  // NOT silently win. `null` means "the URL had nothing to say", exactly
  // mirroring `parsePageSizeParam`'s own null-means-absent-or-invalid
  // contract. Threaded down as a plain prop (`MapTab` → `ListTab` →
  // `useListFeatures`), the same shape `isUrlSynced`/`isReady` already
  // uses for `useMapFeatures`/`useGlobalMapFacets` — not a new mechanism.
  const [pageSizeFromUrl, setPageSizeFromUrl] = useState<number | null>(null)

  useEffect(() => {
    if (hasReadInitialUrl.current) return
    hasReadInitialUrl.current = true

    const params = searchParamsRef.current

    const layersFromUrl = parseLayersParam(params.get(LAYERS_PARAM))
    const featureFromUrl = parseFeatureParam(params.get(FEATURE_PARAM))

    // A deep-linked record's own entity type must end up an active layer —
    // otherwise quick-view Previous/Next (which walks `MapTab`'s
    // layer-filtered feature list) never sees it. Widened (never replaced)
    // against whatever `?layers=` itself resolved to, or against the
    // store's own current layers when there's no `?layers=` param at all.
    // This has to be computed and dispatched as ONE value, in this same
    // pass, before `pendingLayersOverride` is set below — see this
    // function's own doc comment for why a later, separate widening dispatch
    // is exactly what broke the write-effect guard before.
    let layersToApply = layersFromUrl
    if (featureFromUrl) {
      const baseLayers = layersFromUrl ?? activeLayers
      if (!baseLayers.includes(featureFromUrl.entityType)) {
        layersToApply = [...baseLayers, featureFromUrl.entityType]
      }
      // else: baseLayers already covers the deep-linked entity type, so
      // layersToApply stays whatever it was initialized to above — nothing
      // further to compute here.
    }

    if (layersToApply) {
      pendingLayersOverride.current = layersToApply
      dispatch(setActiveLayers(layersToApply))
    }

    const filtersFromUrl = parseFiltersParams(params)
    if (Object.keys(filtersFromUrl).length > 0) {
      pendingFiltersOverride.current = filtersFromUrl
      dispatch(setFilters(filtersFromUrl))
    }

    if (featureFromUrl) {
      pendingFeatureOverride.current = featureFromUrl.id
      dispatch(selectFeature(featureFromUrl))
    }

    const tabFromUrl = parseTabParam(params.get(TAB_PARAM))
    if (tabFromUrl) {
      pendingTabOverride.current = tabFromUrl
      dispatch(setActiveTab(tabFromUrl))
    }

    // Task 9 — `page`/`pageSize` mirror `tab` exactly: read once here (never
    // re-read on a later render, same as every other param in this hook),
    // and left alone — no dispatch at all — when absent or invalid, so an
    // untouched `?page=`/`?pageSize=` never clobbers whatever `currentPage`/
    // `pageSize` already holds (a value `useListFeatures`'s own mount effect
    // may resolve moments later, e.g. from a persisted `ui-preferences`
    // page-size choice — see that hook's own doc comment).
    const pageFromUrl = parsePageParam(params.get(PAGE_PARAM))
    if (pageFromUrl !== null) {
      pendingPageOverride.current = pageFromUrl
      dispatch(setListPage(pageFromUrl))
    }

    const pageSizeParam = parsePageSizeParam(params.get(PAGE_SIZE_PARAM))
    if (pageSizeParam !== null) {
      pendingPageSizeOverride.current = pageSizeParam
      dispatch(setListPageSize(pageSizeParam))
    }
    // Recorded unconditionally (including the null/absent case) — see this
    // state's own comment above for why `useListFeatures` needs the real
    // presence signal, not just the dispatch above.
    setPageSizeFromUrl(pageSizeParam)

    setIsReady(true)
    // `dispatch` is the only dependency, same reasoning as the ancestor hook:
    // this is a plain `.ts` file (`react-hooks/exhaustive-deps` only lints
    // `.tsx` here), and every URL value is deliberately read once via the
    // ref (or, for `activeLayers`, this render's own selector snapshot —
    // still accurate here since no dispatch has touched it yet on this very
    // first run) rather than the closed-over reactive values in general.
  }, [dispatch])

  useEffect(() => {
    if (!hasReadInitialUrl.current) return

    if (pendingLayersOverride.current) {
      if (activeLayers.join(",") !== pendingLayersOverride.current.join(","))
        return
      pendingLayersOverride.current = null
    }

    if (pendingFiltersOverride.current) {
      if (!filtersMatch(filters, pendingFiltersOverride.current)) return
      pendingFiltersOverride.current = null
    }

    if (pendingFeatureOverride.current !== undefined) {
      if (selectedFeatureId !== pendingFeatureOverride.current) return
      pendingFeatureOverride.current = undefined
    }

    if (pendingTabOverride.current) {
      if (activeTab !== pendingTabOverride.current) return
      pendingTabOverride.current = null
    }

    if (pendingPageOverride.current !== null) {
      if (currentPage !== pendingPageOverride.current) return
      pendingPageOverride.current = null
    }

    if (pendingPageSizeOverride.current !== null) {
      if (pageSize !== pendingPageSizeOverride.current) return
      pendingPageSizeOverride.current = null
    }

    const next = new URLSearchParams(searchParamsRef.current)
    applyLayersParam(next, activeLayers)
    applyFiltersParams(next, filters)
    applyFeatureParam(next, selectedFeatureId)
    applyTabParam(next, activeTab)
    applyPageParam(next, currentPage)
    applyPageSizeParam(next, pageSize)

    // Idempotency guard, same shape as the ancestor hook's: skip the call
    // entirely once the URL already says what we're about to write —
    // without it, `setSearchParams` unconditionally navigates even to an
    // identical value.
    if (paramsEqual(searchParamsRef.current, next)) return

    setSearchParams(
      (prev) => {
        const merged = new URLSearchParams(prev)
        applyLayersParam(merged, activeLayers)
        applyFiltersParams(merged, filters)
        applyFeatureParam(merged, selectedFeatureId)
        applyTabParam(merged, activeTab)
        applyPageParam(merged, currentPage)
        applyPageSizeParam(merged, pageSize)
        return merged
      },
      { replace: true }
    )
  }, [
    activeLayers,
    filters,
    selectedFeatureId,
    activeTab,
    currentPage,
    pageSize,
    setSearchParams
  ])

  return { isReady, pageSizeFromUrl }
}

export {
  parseLayersParam,
  parseNumberListParam,
  parseStringListParam,
  parseStringParam,
  parseIsoDateParam,
  parseFeatureParam,
  parseFiltersParams,
  parseTabParam,
  LAYERS_PARAM,
  FEATURE_PARAM,
  TAB_PARAM
}
